import { FilterId, type PrimaryFilterId } from '../../filters/types/filters';
import { isSelectableChartType } from '../ChartConfig/utils/is-selectable-chart-type';
import { getAvailableMetrics } from '../ChartConfig/utils/metric-mapping';
import { getAvailableSegment } from '../ChartConfig/utils/segment-mapping';
import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../PageConfigProvider/constants';
import type { TabContentAction } from '../PageConfigProvider/page-reducer';
import type {
  ChartType,
  SelectableChartType,
} from '../PageConfigProvider/types/chart';
import type {
  SecondaryDimensionOption,
  SecondaryDimensionValues,
} from '../PageConfigProvider/types/dimension';
import type {
  DimensionSource,
  PlotFilterType,
  PlotOptions,
  PositionedItem,
} from '../PageConfigProvider/types/item';
import type { MetricValues } from '../PageConfigProvider/types/metric';

export interface LLMTabContentForDispatch {
  items: LLMPositionedItemForDispatch[];
  grid: { rows: number; columns: number };
  secondaryFilters: { id: string }[];
}

export interface LLMPositionedItemForDispatch {
  title: string | null;
  position: { x: number; y: number; width: number; height: number };
  plot?: {
    chartType: string;
    defaultMetricOptions: string[];
    secondaryDimension?: string;
    plotFilters?: Array<{ type: string; value: number }>;
    dimensionSource?: {
      type: 'PRIMARY_DIMENSION' | 'TOP_SEGMENTS';
      segment?: SecondaryDimensionValues;
    };
    color?: string;
    showDataLabels?: boolean;
    showZebraStripe?: boolean;
  };
}

function validateAndCorrectPlotOptions(
  plot: LLMPositionedItemForDispatch['plot'],
  primaryFilterId: PrimaryFilterId
): PlotOptions | undefined {
  if (!plot) return undefined;

  let chartType = plot.chartType as ChartType;
  const requestedMetrics = plot.defaultMetricOptions as MetricValues[];
  const requestedSecondaryDimension = plot.secondaryDimension as
    | SecondaryDimensionValues
    | undefined;

  if (!isSelectableChartType(chartType)) {
    chartType = 'bar';
  }

  const availableMetricGroups = getAvailableMetrics({
    primaryFilterId,
    chartType: chartType as SelectableChartType,
  });
  const availableMetrics = availableMetricGroups.flatMap((g) =>
    g.options.map((o) => o.value)
  );

  let validMetrics = requestedMetrics.filter((m) =>
    availableMetrics.includes(m)
  );

  if (validMetrics.length === 0 && availableMetrics.length > 0) {
    const firstMetric = availableMetrics[0];
    if (firstMetric) {
      validMetrics = [firstMetric];
    } else {
      return undefined;
    }
  }

  let secondaryDimension = requestedSecondaryDimension;

  const chartTypesWithSecondaryDimension: ChartType[] = [
    'grouped-bar',
    'stacked-bar',
    'box',
    'treemap',
  ];
  const shouldHaveSecondaryDimension =
    chartTypesWithSecondaryDimension.includes(chartType);

  if (secondaryDimension && shouldHaveSecondaryDimension) {
    const primaryMetric = validMetrics[0];
    if (primaryMetric) {
      const availableSegments = getAvailableSegment({
        chartType,
        primaryFilterId,
        metric: primaryMetric,
      });
      const availableSegmentValues = availableSegments.map(
        (s: SecondaryDimensionOption) => s.value
      );

      if (!availableSegmentValues.includes(secondaryDimension)) {
        if (availableSegmentValues.length > 0) {
          secondaryDimension =
            availableSegmentValues[0] as SecondaryDimensionValues;
        } else {
          secondaryDimension = undefined;
        }
      }
    }
  } else if (secondaryDimension && !shouldHaveSecondaryDimension) {
    secondaryDimension = undefined;
  }

  if (!secondaryDimension && shouldHaveSecondaryDimension) {
    const primaryMetric = validMetrics[0];
    if (primaryMetric) {
      const availableSegments = getAvailableSegment({
        chartType,
        primaryFilterId,
        metric: primaryMetric,
      });
      if (availableSegments.length > 0) {
        const firstSegment = availableSegments[0];
        if (firstSegment) {
          secondaryDimension = firstSegment.value;
        }
      }
    }
  }

  let dimensionSource: DimensionSource | undefined;
  if (plot.dimensionSource) {
    if (plot.dimensionSource.type === 'PRIMARY_DIMENSION') {
      dimensionSource = 'primary';
    } else if (
      plot.dimensionSource.type === 'TOP_SEGMENTS' &&
      plot.dimensionSource.segment
    ) {
      dimensionSource = plot.dimensionSource
        .segment as SecondaryDimensionValues;
    }
  }

  const finalConfig: PlotOptions = {
    chartType,
    defaultMetricOptions: validMetrics,
    secondaryDimension,
    ...(dimensionSource && { dimensionSource }),
    ...(plot.plotFilters &&
      plot.plotFilters.length > 0 && {
        plotFilters: plot.plotFilters.map((f) => ({
          type: f.type as PlotFilterType,
          value: f.value,
        })),
      }),
    ...(plot.color && { color: plot.color }),
    ...(plot.showDataLabels !== undefined && {
      showDataLabels: plot.showDataLabels,
    }),
    ...(plot.showZebraStripe !== undefined && {
      showZebraStripe: plot.showZebraStripe,
    }),
  };

  return finalConfig;
}

function convertLLMItemToPositionedItem(
  item: LLMPositionedItemForDispatch,
  primaryFilterId: PrimaryFilterId
): PositionedItem {
  const plot = validateAndCorrectPlotOptions(item.plot, primaryFilterId);

  return {
    id: crypto.randomUUID(),
    title: item.title,
    position: item.position,
    plot,
  };
}

export function getValidatedPositionedItemsFromLlmContent(
  content: LLMTabContentForDispatch,
  primaryFilterId: PrimaryFilterId
): PositionedItem[] {
  return content.items
    .map((item) => convertLLMItemToPositionedItem(item, primaryFilterId))
    .filter((item) => {
      if (!item.plot) {
        return false;
      }
      if (
        !item.plot.defaultMetricOptions ||
        item.plot.defaultMetricOptions.length === 0
      ) {
        return false;
      }
      return true;
    });
}

/** One chart on a new custom page: tall enough to read without adding grid rows first. */
const GLOBAL_ASSISTANT_SINGLE_CHART_CUSTOM_PAGE_CHART_HEIGHT_ROWS = 22;

/**
 * Rebuilds LLM-shaped tab content from a validated `PositionedItem` so global-assistant
 * flows can re-dispatch through `applyLlmTabContentToTabDispatch` (e.g. open single chart on a custom page).
 */
export function buildLlmTabContentFromPositionedItemForGlobalAssistant(
  item: PositionedItem
): LLMTabContentForDispatch {
  const plot = item.plot;
  const llmPlot: LLMPositionedItemForDispatch['plot'] = (() => {
    if (!plot) {
      return undefined;
    }
    const dimensionSource = (() => {
      const ds = plot.dimensionSource;
      if (ds === undefined) {
        return undefined;
      }
      if (ds === 'primary') {
        return { type: 'PRIMARY_DIMENSION' as const };
      }
      return {
        type: 'TOP_SEGMENTS' as const,
        segment: ds as SecondaryDimensionValues,
      };
    })();

    return {
      chartType: plot.chartType ?? 'bar',
      defaultMetricOptions: plot.defaultMetricOptions ?? [],
      ...(plot.secondaryDimension !== undefined && {
        secondaryDimension: plot.secondaryDimension,
      }),
      ...(plot.plotFilters &&
        plot.plotFilters.length > 0 && {
          plotFilters: plot.plotFilters.map((f) => ({
            type: String(f.type),
            value: f.value,
          })),
        }),
      ...(dimensionSource !== undefined && { dimensionSource }),
      ...(plot.color !== undefined && { color: plot.color }),
      ...(plot.showDataLabels !== undefined && {
        showDataLabels: plot.showDataLabels,
      }),
      ...(plot.showZebraStripe !== undefined && {
        showZebraStripe: plot.showZebraStripe,
      }),
    };
  })();

  return {
    items: [
      {
        title: item.title,
        position: {
          x: 0,
          y: 0,
          width: DEFAULT_GRID_COLUMNS,
          height: Math.min(
            GLOBAL_ASSISTANT_SINGLE_CHART_CUSTOM_PAGE_CHART_HEIGHT_ROWS,
            DEFAULT_GRID_ROWS
          ),
        },
        plot: llmPlot,
      },
    ],
    grid: {
      rows: DEFAULT_GRID_ROWS,
      columns: DEFAULT_GRID_COLUMNS,
    },
    secondaryFilters: [],
  };
}

export function applyLlmTabContentToTabDispatch(
  dispatch: (action: TabContentAction) => void,
  content: LLMTabContentForDispatch,
  primaryFilterId: PrimaryFilterId
): void {
  const validatedItems = getValidatedPositionedItemsFromLlmContent(
    content,
    primaryFilterId
  );
  dispatch({
    type: 'SET_ITEMS',
    payload: validatedItems,
  });
  dispatch({
    type: 'SET_GRID',
    payload: {
      rows: content.grid.rows,
      columns: content.grid.columns ?? DEFAULT_GRID_COLUMNS,
    },
  });
}

export const PRIMARY_FILTER_BY_AI_ENTITY: Record<
  'company' | 'geography' | 'role',
  PrimaryFilterId
> = {
  company: FilterId.COMPANY,
  geography: FilterId.GEOGRAPHY,
  role: FilterId.ROLE,
};
